import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { Subscription } from 'rxjs/Subscription';
import { AlertService } from 'sce-core';
import { ConfirmDialogService } from 'sce-core';
import { NavigationService } from '../../../../core/navigation.service';
import { ResourceService } from 'sce-core';
import { NEW_SEARCH_EVENT } from '../../search-constants';
import { SearchService } from '../../search.service';
import { SearchEvent } from '../../models/search-event';
import { PaginationEvent } from './../../../events/pagination-event';
import { PageDefinition } from '../../models/page-definition.model';
import { TableRow } from '../../models/table-row.model';
import { SearchableEntityConfig } from '../../../models/searchable-entity-config';
import { DynamicEntityDataService } from '../../../services/searchable-entity/dynamic-entity/dynamic-entity-data.service';

@Component({
  selector: 'lfwms-result-data-mapper',
  templateUrl: './result-data-mapper.component.html',
  styleUrls: ['./result-data-mapper.component.css']
})
export class ResultDataMapperComponent implements OnInit, OnDestroy {

  // Metadata Map
  private metadataMap: any;
  // Search Event Subscription
  private eventSubscription: Subscription;
  private dragCanvas: any;
  // Flag to disable dropping to source drop zone
  private isDragged: boolean = false;
  private entityContext: any;
  // Page Metadata Object Reference
  public pageDefinition: PageDefinition;
  // Flag to store if Data Table is initialized
  public isGridReady: boolean = false;
  // Column Definitions Array
  public columnDefinitions: Array<any>;
  // Flag to select all records
  public isSelectedAll: boolean = false;
  // Object Reference to hold Formatted Server Response
  public pageRows: Array<any>;
  public displayedRows: Array<any>;
  public dynamicDummyRows: Array<any>;
  public staticDummyRows: Array<any> = [{}, {}, {}, {}, {}, {}, {}, {}, {}, {}];
  // Loader Flag. Whenever true, Loader/Spinner should be displayed
  public isLoading: boolean = true;
  public showCustomizer: boolean = false;
  public searchService: SearchService;
  public selectedRowCount: number = 0;
  public showSelectOption: boolean = true;

  // Input Entity Context
  @Input() public entityConfig: SearchableEntityConfig;
  // Context Identifier
  @Input() public contextIdentifier: string;
  @Input() public dataService: DynamicEntityDataService;
  @Output() private customEvent: EventEmitter<any> = new EventEmitter();

  @ViewChild('relativeHeaderContainer', { read: ElementRef }) private rContainerRef: ElementRef;

  constructor(private resourceService: ResourceService,
    private alertService: AlertService,
    private confirmDialogService: ConfirmDialogService,
    protected navService: NavigationService) { }

  public ngOnInit() {
    this.entityContext = this.entityConfig.getEntityContextObject()[this.contextIdentifier];
    this.searchService = this.entityContext.searchService;
    if (this.searchService.getMetadata()) {
      this.buildColumnDefinitions();
    } else {
      this.alertService.error('Metadata not available');
    }
    // Below subscription subscribes to various Search Events published from respective Search Service
    this.eventSubscription = this.searchService.receiveEvent()
      .subscribe((event: SearchEvent) => {
        if (event.type === NEW_SEARCH_EVENT) {
          this.isLoading = true;
          this.loadData();
        }
      });
  }

  // Method to build ColumnData Object from MetaData Response
  private buildColumnDefinitions() {
    const metadata: any = this.searchService.getMetadata();
    const columnList: Array<any> = metadata['columnList'];
    columnList
      .sort((a: any, b: any) => {
        return (a['index'] < b['index']) ? -1 : (a['index'] > b['index']) ? 1 : 0;
      });
    let columnCount = columnList.length;
    if (columnCount > 10
      && !this.searchService.searchParams.isFullFetch
        && (this.searchService.searchParams.columnCount > 0)) {
      columnCount = this.searchService.searchParams.columnCount;
    }
    // Initializing Metadata Map
    this.metadataMap = {};
    // Initializing ColumnData Array
    this.columnDefinitions = [ {} ];
    // Populating ColumnData Array with Column Objects
    for (let x: number = 0; x < columnCount; x++) {
      const key: string = columnList[x].propertyName;
      this.metadataMap[key] = columnList[x];
      const columnDefinition: any = new Object();
      columnDefinition.isRestricted = (this.metadataMap[key].displayType === 'H');
      columnDefinition.field = columnDefinition.colId = key;
      columnDefinition.index = columnList[x].index;
      columnDefinition.minWidth = 150;
      columnDefinition.maxWidth = 450;
      columnDefinition.width = 250;
      columnDefinition.editable = false;
      columnDefinition.sortOrder = -1;
      columnDefinition.property = columnList[x].propertyName;
      columnDefinition.name = this.resourceService.get(columnList[x].elementId);
      columnDefinition.type = columnList[x].type;
      columnDefinition.values = columnList[x].values;
      columnDefinition.isHidden = false;
      this.columnDefinitions.push(columnDefinition);
    }
    this.isGridReady = true;
    this.loadData();
  }

  private loadData() {
    // Updating Local Page Definition
    this.pageDefinition = this.searchService.getPagedData().pageDefinition;
    // Updating Local Page Data
    this.pageRows = this.searchService.getPagedData().pageRows;
    this.isSelectedAll = false;
    this.selectedRowCount = this.showSelectOption ? 0 : -1;
    this.dynamicDummyRows = [];
    if (this.pageRows.length < this.pageDefinition.pageSize) {
      for (let i = this.pageRows.length; i < this.pageDefinition.pageSize; i++) {
        this.dynamicDummyRows.push({});
      }
    }
    this.isLoading = false;
  }

  public ngOnDestroy() {
    if (this.eventSubscription) {
      this.eventSubscription.unsubscribe();
    }
  }

  public getCellDisplayValue(row: any, key: string) {
    return (row.cells[key]) ? row.cells[key].cVal : '';
  }

  public onPageChanged(paginationEvent: PaginationEvent) {
    const triggerPagination: any = () => {
      this.isLoading = true;
      this.pageDefinition.pageNumber = paginationEvent.pageNumber;
      this.pageDefinition.pageSize = paginationEvent.pageSize;
      this.searchService.performSearch();
    };
    if (this.showSelectOption && this.selectedRowCount > 0) {
      this.confirmDialogService
        .confirmDialog('Current Selections will be lost. Proceed ?')
          .subscribe((isConfirm: boolean) => {
            if (isConfirm) {
              triggerPagination();
            }
          });
    } else {
      triggerPagination();
    }
  }

  public toggleSelectAll(selectAll?: boolean) {
    if (this.selectedRowCount > -1) {
      if (selectAll === undefined) {
        this.isSelectedAll = !this.isSelectedAll;
      } else {
        this.isSelectedAll = selectAll;
      }
      this.pageRows
        .forEach((row: any, index: number) => {
          row.isSelected = this.pageRows[index].isSelected = this.isSelectedAll;
        });
      this.selectedRowCount = this.isSelectedAll ? this.pageRows.length : 0;
    }
  }

  public toggleSelect(rowIndex: number, row: TableRow) {
    if (this.selectedRowCount > -1) {
      this.pageRows[rowIndex].isSelected
        = this.pageRows[rowIndex].isSelected = !this.pageRows[rowIndex].isSelected;
      this.pageRows[rowIndex].isSelected ? this.selectedRowCount++ : this.selectedRowCount--;
      this.isSelectedAll = this.selectedRowCount === this.pageRows.length;
    }
  }

  public toggleColumnSort(column: any) {
    this.pageDefinition.sortColumn = column.property;
    this.pageDefinition.sortOrder
      = (column.sortOrder === -1 || column.sortOrder === 0) ? 1 : 0;
    this.columnDefinitions
      .forEach((columnDefinition: any) => {
        if (columnDefinition.property !== column.property) {
          columnDefinition.sortOrder = -1;
        } else {
          columnDefinition.sortOrder = this.pageDefinition.sortOrder;
        }
      });
    this.searchService.performSearch();
  }

  public showUserPreferencesPanel() {
    if (!this.isLoading) {
      this.showCustomizer = true;
    }
  }

  public handleColumnResize($event: any) {}

  public handleDragDrop($event: any, rowIndex?: number) {
    switch ($event.type) {
      case 'dragstart': {
        this.isDragged = true;
        this.pageRows[rowIndex].isSelected = true;
        $event.dataTransfer
          .setData('text/plain',
            JSON.stringify({ dragIndex: rowIndex,
              dragSource: this.contextIdentifier }));
        $event.srcElement.closest('tr.row-data').classList.add('highlight-row');
        this.dragCanvas = document.createElement('canvas');
        this.dragCanvas.id = 'dragCanvas';
        this.dragCanvas.height = 40;
        this.dragCanvas.width = 400;
        const dragContext = this.dragCanvas.getContext('2d');
        dragContext.fillStyle = 'blue';
        dragContext.fillRect(0, 0, 500, 40);
        const dragImage = new Image();
        dragImage.src = this.dragCanvas.toDataURL('image/jpg', 1);
        dragImage.id = 'dragImage';
        $event.dataTransfer.setDragImage(dragImage, 0, 0);
        break;
      }
      case 'drag': {
        break;
      }
      case 'dragenter': {
        break;
      }
      case 'dragover': {
        if (!this.isDragged) {
          $event.preventDefault();
          this.pageRows[rowIndex].isSelected = true;
          $event.srcElement.closest('tr.row-data').classList.add('highlight-row');
        }
        break;
      }
      case 'dragleave': {
        if (!this.isDragged) {
          this.pageRows[rowIndex].isSelected = false;
          $event.srcElement.closest('tr.row-data').classList.remove('highlight-row');
        }
        break;
      }
      case 'drop': {
        if (!this.isDragged) {
          $event.preventDefault();
          this.pageRows[rowIndex].isSelected = false;
          $event.srcElement.closest('tr.row-data').classList.remove('highlight-row');
          const DnDEvent: any = JSON.parse($event.dataTransfer.getData('text/plain'));
          DnDEvent['dropIndex'] = rowIndex;
          DnDEvent['dropTarget'] = this.contextIdentifier;
          this.setRelationship(DnDEvent);
        }
        break;
      }
      case 'dragend': {
        if (this.isDragged) {
          $event.srcElement.closest('tr.row-data').classList.remove('highlight-row');
          this.isDragged = false;
          this.pageRows[rowIndex].isSelected = false;
        }
        break;
      }
      default: {
        break;
      }
    }
  }

  private setRelationship(dndEvent: any) {
    const rKey: string = dndEvent.dragSource + '-' + dndEvent.dropTarget;
    const relationshipParam: any = this.entityConfig.getRelationshipParams()[rKey];
    const $event: any = { rKey: rKey, rPageRow: new TableRow() };
    $event['rPageRow'].cells = {};
    const isLTR: boolean = (relationshipParam.leftContext === dndEvent.dragSource);
    const leftEntityContext: any
      = this.entityConfig
        .getEntityContext(isLTR ? dndEvent.dragSource : dndEvent.dropTarget);
    const rightEntityContext: any
      = this.entityConfig
        .getEntityContext(isLTR ? dndEvent.dropTarget : dndEvent.dragSource);
    const leftPageRow: TableRow
      = leftEntityContext.searchService.getPagedData()
          .pageRows[isLTR ? dndEvent.dragIndex : dndEvent.dropIndex];
    const rightPageRow: TableRow
      = rightEntityContext.searchService.getPagedData()
          .pageRows[isLTR ? dndEvent.dropIndex : dndEvent.dragIndex];
    relationshipParam.leftKeys
      .map((leftKey: string) => {
        $event['rPageRow'].cells[leftKey] = leftPageRow.cells[leftKey];
      });
    relationshipParam.rightKeys
      .map((rightKey: string) => {
        $event['rPageRow'].cells[rightKey] = rightPageRow.cells[rightKey];
      });
    this.confirmDialogService
      .confirmDialog(this.resourceService.get(relationshipParam.leftKeyLabel)
        + ' [' + $event['rPageRow'].cells[relationshipParam.leftMasterKey].cVal
        + '] will be assigned to ' + this.resourceService.get(relationshipParam.rightKeyLabel)
        + ' [' + $event['rPageRow'].cells[relationshipParam.rightMasterKey].cVal
        + ']. Continue ?')
        .subscribe((isConfirmed: boolean) => {
          if (isConfirmed) {
            if ($event['rKey'] === 'location-sku') {
              if (this.validateClientSideInputs()) {
                leftPageRow.cells['noOfSkuAssigned'].cVal ++;
                rightPageRow.cells['noOfLocAssigned'].cVal ++;
                this.entityConfig.createCachedRelationship($event);
                this.alertService
                  .success('Assignment created successfully');
              }
            } else {
              this.entityConfig.createCachedRelationship($event);
              this.alertService
                .success('Assignment created successfully');
            }
          }
        });
  }

  public handleRowClick(rowIndex: number) {
    const $event: any = { type: 'ROW_DBL_CLICK' };
    if (this.entityContext.searchService.getPagedData().pageRows[rowIndex]) {
      $event['contextIdentifier'] = this.contextIdentifier;
      $event['pageRow'] = this.entityContext.searchService.getPagedData().pageRows[rowIndex];
    }
    this.customEvent.emit($event);
  }

  private validateClientSideInputs(): boolean {
    const relationshipParam: any
      = this.dataService.entityConfig.getRelationshipParams()['location-sku'];
    const qtyLocMin: number = +relationshipParam.clientProperties['qtyLocMin'].cVal;
    const qtyLocMax: number = +relationshipParam.clientProperties['qtyLocMax'].cVal;
    if (qtyLocMin < 1) {
      this.alertService.error('Please enter a valid minimum quantity');
      return false;
    } else if (qtyLocMax < 1) {
      this.alertService.error('Please enter a valid maximum quantity');
      return false;
    } else if (qtyLocMax < qtyLocMin) {
      this.alertService.error('Minimum quantity should not exceed maximum quantity');
      return false;
    } else {
      return true;
    }
  }

  public onScroll($event: any) {
    this.rContainerRef.nativeElement.scrollLeft = $event.target.scrollLeft;
  }

}



// WEBPACK FOOTER //
// /var/lib/jenkins/workspace/1_SCE_CI_CD_SIT/src/app/shared/search/results/result-data-mapper/result-data-mapper.component.ts